/**
 * Name tag above a remote avatar's head: name, role subtitle in the role colour, a speaking
 * ring. A sprite (always faces the viewer) with a canvas texture redrawn only when the
 * speaking or muted state changes. Beyond 2.5 m it grows with distance (up to 2.2 times at
 * 5.5 m) so it stays readable across a room; it fades out between 8 and 12 m.
 */
import * as THREE from 'three';
import { NAME_TAG_FADE_START_M, ROLE_STYLES, type Role } from '@clinspace/shared';

const W = 512;
const H = 160;
/** Width of the tag in metres up close. */
const TAG_WIDTH_M = 0.5;

export class NameTag {
  readonly sprite: THREE.Sprite;
  private readonly canvas = document.createElement('canvas');
  private readonly texture: THREE.CanvasTexture;
  private speaking = false;
  private muted = false;
  private handRaised = false;

  constructor(
    private name: string,
    private role: Role,
  ) {
    this.canvas.width = W;
    this.canvas.height = H;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    const material = new THREE.SpriteMaterial({
      map: this.texture,
      transparent: true,
      depthTest: true,
      depthWrite: false,
      toneMapped: false,
    });
    this.sprite = new THREE.Sprite(material);
    this.sprite.scale.set(TAG_WIDTH_M, (TAG_WIDTH_M * H) / W, 1);
    this.sprite.center.set(0.5, 0);
    this.sprite.renderOrder = 50;
    this.draw();
  }

  setIdentity(name: string, role: Role): void {
    if (name === this.name && role === this.role) return;
    this.name = name;
    this.role = role;
    this.draw();
  }

  setSpeaking(speaking: boolean): void {
    if (speaking === this.speaking) return;
    this.speaking = speaking;
    this.draw();
  }

  setMuted(muted: boolean): void {
    if (muted === this.muted) return;
    this.muted = muted;
    this.draw();
  }

  setHandRaised(on: boolean): void {
    if (on === this.handRaised) return;
    this.handRaised = on;
    this.draw();
  }

  /** Distance-based fade; call per frame with the distance to the viewer. */
  setDistance(metres: number): void {
    const fadeEnd = NAME_TAG_FADE_START_M + 4;
    const opacity =
      metres <= NAME_TAG_FADE_START_M
        ? 1
        : Math.max(0, 1 - (metres - NAME_TAG_FADE_START_M) / (fadeEnd - NAME_TAG_FADE_START_M));
    (this.sprite.material as THREE.SpriteMaterial).opacity = opacity;
    this.sprite.visible = opacity > 0.02;
    const grow = THREE.MathUtils.clamp(metres / 2.5, 1, 2.2);
    this.sprite.scale.set(TAG_WIDTH_M * grow, ((TAG_WIDTH_M * H) / W) * grow, 1);
  }

  private draw(): void {
    const ctx = this.canvas.getContext('2d')!;
    const style = ROLE_STYLES[this.role];
    ctx.clearRect(0, 0, W, H);
    // Pill background.
    ctx.fillStyle = 'rgba(18, 24, 34, 0.88)';
    roundRect(ctx, 40, 24, W - 80, H - 48, 36);
    ctx.fill();
    // Speaking ring / muted mark.
    ctx.beginPath();
    ctx.arc(84, H / 2, 20, 0, Math.PI * 2);
    ctx.fillStyle = this.muted ? '#7f8a98' : this.speaking ? '#4cd97b' : style.hex;
    ctx.fill();
    if (this.speaking && !this.muted) {
      ctx.beginPath();
      ctx.arc(84, H / 2, 30, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(76, 217, 123, 0.7)';
      ctx.lineWidth = 6;
      ctx.stroke();
    }
    if (this.muted) {
      ctx.strokeStyle = '#ff9aa8';
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(70, H / 2 - 14);
      ctx.lineTo(98, H / 2 + 14);
      ctx.stroke();
    }
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.fillStyle = '#f2f6fb';
    ctx.font = '600 44px "Inter", "Segoe UI", Roboto, system-ui, sans-serif';
    ctx.fillText(this.name, 124, H / 2 - 20, W - 180);
    ctx.fillStyle = style.hex;
    ctx.font = '500 30px "Inter", "Segoe UI", Roboto, system-ui, sans-serif';
    ctx.fillText(this.role, 124, H / 2 + 26, W - 180);
    if (this.handRaised) {
      ctx.fillStyle = '#ffd37a';
      ctx.beginPath();
      ctx.arc(W - 78, H / 2, 30, 0, Math.PI * 2);
      ctx.fill();
      ctx.font = '40px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#121822';
      ctx.fillText('✋', W - 78, H / 2 + 2);
    }
    this.texture.needsUpdate = true;
  }

  dispose(): void {
    this.texture.dispose();
    (this.sprite.material as THREE.Material).dispose();
  }
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
